import React, { useCallback, useRef, useState } from 'react';
import { Table, BarChart3, GitMerge } from 'lucide-react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    PointElement,
    LineElement,
    ArcElement
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';

import DataTable from './DataTable';
import GroupedView from './GroupedView';
import { useMediaQuery } from '../hooks/useMediaQuery';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    PointElement,
    LineElement,
    ArcElement
);

/**
 * Universal data visualization component that works with any handler's metadata.
 * Automatically detects available visualizations and provides tab-based interface.
 */
const DataVisualization = ({ metadata, onExport, defaultView = 'table', customCharts = null, forcedView = null }) => {
    const [activeView, setActiveView] = useState(defaultView);
    const [chartCarouselIndex, setChartCarouselIndex] = useState(0);
    const chartCarouselRef = useRef(null);
    const isMobile = useMediaQuery('(max-width: 767px)');

    // Effective view is forcedView if provided, otherwise internal activeView
    const currentView = forcedView || activeView;

    const { events, aggregations, summary, viz_config } = metadata;
    const hasAggregations = Boolean(aggregations && Object.keys(aggregations).length > 0);
    const normalizedCustomCharts = (
        React.isValidElement(customCharts) ||
        (Array.isArray(customCharts) && customCharts.length > 0)
    ) ? customCharts : null;

    // Determine available views
    const enableGrouping = Boolean(viz_config?.enable_grouping && hasAggregations);
    const enableCharts = Boolean(hasAggregations || normalizedCustomCharts);
    const allowedCharts = Array.isArray(viz_config?.chart_options) ? viz_config.chart_options : null;
    const allows = (key) => !allowedCharts || allowedCharts.includes(key);

    const getChartStep = useCallback((container) => {
        if (!container) return 1;
        const firstSlide = container.querySelector('[data-carousel-item="chart"]');
        if (!firstSlide) return Math.max(container.clientWidth, 1);
        const styles = window.getComputedStyle(container);
        const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return Math.max(firstSlide.getBoundingClientRect().width + gap, 1);
    }, []);

    const handleChartCarouselScroll = useCallback((e) => {
        const container = e.currentTarget;
        const step = getChartStep(container);
        const idx = Math.round(container.scrollLeft / step);
        setChartCarouselIndex(Math.max(0, idx));
    }, [getChartStep]);

    const scrollToChart = useCallback((index) => {
        const container = chartCarouselRef.current;
        if (!container) return;
        const step = getChartStep(container);
        container.scrollTo({ left: step * index, behavior: 'smooth' });
        setChartCarouselIndex(index);
    }, [getChartStep]);

    if (!metadata || !metadata.events || metadata.events.length === 0) {
        return (
            <div className="text-center py-8 text-gray-500">
                No visualization data available
            </div>
        );
    }

    // Render charts based on primary chart type and available aggregations
    const renderCharts = () => {
        if (!aggregations && !normalizedCustomCharts) return null;

        const charts = [];
        const hideTruckEventCountChart = metadata.handler_type === 'downtime_analysis';

        if (aggregations) {
            // Timeline chart (Line)
            if (allows('timeline') && aggregations.by_timeline && Object.keys(aggregations.by_timeline).length > 0) {
                const rawTimelineEntries = Object.entries(aggregations.by_timeline);
                const MAX_TIMELINE_POINTS = 24;
                let timelineLabels = rawTimelineEntries.map(([label]) => label);
                let timelineValues = rawTimelineEntries.map(([, value]) => Number(value) || 0);

                // Downsample dense timelines into fixed-size windows to show overall trend.
                if (rawTimelineEntries.length > MAX_TIMELINE_POINTS) {
                    const bucketSize = Math.ceil(rawTimelineEntries.length / MAX_TIMELINE_POINTS);
                    const compactLabels = [];
                    const compactValues = [];

                    for (let i = 0; i < rawTimelineEntries.length; i += bucketSize) {
                        const bucket = rawTimelineEntries.slice(i, i + bucketSize);
                        const midIndex = Math.floor((bucket.length - 1) / 2);
                        const label = bucket[midIndex][0];
                        const avgValue = bucket.reduce((sum, [, v]) => sum + (Number(v) || 0), 0) / bucket.length;

                        compactLabels.push(label);
                        compactValues.push(Number(avgValue.toFixed(2)));
                    }

                    timelineLabels = compactLabels;
                    timelineValues = compactValues;
                }

                const timelineData = {
                    labels: timelineLabels,
                    datasets: [{
                        label: 'Events Over Time',
                        data: timelineValues,
                        borderColor: 'rgb(59, 130, 246)',
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        borderWidth: 3,
                        pointBackgroundColor: '#fff',
                        pointBorderColor: 'rgb(59, 130, 246)',
                        pointBorderWidth: 2,
                        pointRadius: timelineLabels.length > 16 ? 2 : 4,
                        pointHoverRadius: timelineLabels.length > 16 ? 4 : 7,
                        pointHoverBackgroundColor: 'rgb(59, 130, 246)',
                        pointHoverBorderColor: '#fff',
                        pointHoverBorderWidth: 2,
                        tension: 0.4,
                        fill: true
                    }]
                };

                charts.push(
                    <div key="timeline" className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Events Timeline</h4>
                        <div className="h-64">
                            <Line
                                data={timelineData}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: { display: false },
                                        tooltip: {
                                            backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                            padding: 12,
                                            cornerRadius: 8,
                                            displayColors: false
                                        }
                                    },
                                    scales: {
                                        y: {
                                            beginAtZero: true,
                                            grid: { color: 'rgba(0, 0, 0, 0.03)' },
                                            ticks: { font: { size: 11 } }
                                        },
                                        x: {
                                            grid: { display: false },
                                            ticks: {
                                                font: { size: 10 },
                                                maxRotation: 45,
                                                minRotation: 0
                                            }
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                );
            }

            // Truck distribution chart (Bar)
            if (!hideTruckEventCountChart && allows('by_truck') && aggregations.by_truck && Object.keys(aggregations.by_truck).length > 0) {
                // Only show top 10 for readability
                const truckEntries = Object.entries(aggregations.by_truck).slice(0, 10);

                // Specific labels for SLA compliance
                const isSLA = metadata.handler_type === 'sla_compliance';
                const truckChartTitle = isSLA ? "Top Trucks by Breaches" : "Top Trucks by Event Count";
                const truckDatasetLabel = isSLA ? "Breaches by Truck" : "Events by Truck";

                const truckData = {
                    labels: truckEntries.map(([truck]) => truck),
                    datasets: [{
                        label: truckDatasetLabel,
                        data: truckEntries.map(([, count]) => count),
                        backgroundColor: truckEntries.map((_, idx) =>
                            summary?.outliers?.includes(truckEntries[idx][0])
                                ? 'rgba(239, 68, 68, 0.7)'
                                : 'rgba(59, 130, 246, 0.7)'
                        ),
                        borderColor: 'rgb(59, 130, 246)',
                        borderWidth: 1
                    }]
                };

                charts.push(
                    <div key="trucks" className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">{truckChartTitle}</h4>
                        <div className="h-64">
                            <Bar
                                data={truckData}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: { display: false },
                                        tooltip: {
                                            backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                            padding: 10,
                                            cornerRadius: 8
                                        }
                                    },
                                    scales: {
                                        y: {
                                            beginAtZero: true,
                                            grid: { color: 'rgba(0, 0, 0, 0.03)' },
                                            ticks: { font: { size: 11 } }
                                        },
                                        x: {
                                            grid: { display: false },
                                            ticks: { font: { size: 11 } }
                                        }
                                    },
                                    borderRadius: 6
                                }}
                            />
                        </div>
                    </div>
                );
            }

            // System distribution chart (Doughnut)
            const isPrecursorPattern = metadata.handler_type === 'precursor_pattern';
            const systemAggregationSource = aggregations.by_system;

            if (allows('by_system') && systemAggregationSource && Object.keys(systemAggregationSource).length > 0) {
                const TOP_SYSTEM_CATEGORIES = 12;
                const sortedSystems = Object.entries(systemAggregationSource)
                    .sort((a, b) => b[1] - a[1]);
                const topSystems = sortedSystems.slice(0, TOP_SYSTEM_CATEGORIES);
                const remainingCount = sortedSystems
                    .slice(TOP_SYSTEM_CATEGORIES)
                    .reduce((sum, [, count]) => sum + count, 0);
                const topMap = new Map(topSystems);

                if (remainingCount > 0) {
                    topMap.set('Other', (topMap.get('Other') || 0) + remainingCount);
                }

                const systemLabels = Array.from(topMap.keys());
                const systemValues = Array.from(topMap.values());
                const colorPalette = [
                    'rgba(59, 130, 246, 0.8)',
                    'rgba(16, 185, 129, 0.8)',
                    'rgba(249, 115, 22, 0.8)',
                    'rgba(139, 92, 246, 0.8)',
                    'rgba(236, 72, 153, 0.8)',
                    'rgba(20, 184, 166, 0.8)',
                    'rgba(251, 191, 36, 0.8)',
                    'rgba(239, 68, 68, 0.8)',
                    'rgba(34, 197, 94, 0.8)',
                    'rgba(14, 165, 233, 0.8)',
                    'rgba(244, 114, 182, 0.8)',
                    'rgba(168, 85, 247, 0.8)',
                    'rgba(99, 102, 241, 0.8)',
                    'rgba(234, 179, 8, 0.8)',
                    'rgba(6, 182, 212, 0.8)',
                    'rgba(248, 113, 113, 0.8)',
                ];
                const systemColors = systemLabels.map((_, idx) => colorPalette[idx % colorPalette.length]);
                const systemData = {
                    labels: systemLabels,
                    datasets: [{
                        data: systemValues,
                        backgroundColor: systemColors,
                        borderWidth: 2,
                        borderColor: '#fff'
                    }]
                };

                charts.push(
                    <div key="systems" className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">
                            {isPrecursorPattern ? 'Precursor Events by System' : 'Events by System'}
                        </h4>
                        <div className="h-64 flex items-center justify-center">
                            <div className="w-64 h-64">
                                <Doughnut
                                    data={systemData}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        cutout: '70%',
                                        plugins: {
                                            legend: {
                                                display: false
                                            },
                                            tooltip: {
                                                backgroundColor: 'rgba(17, 24, 39, 0.9)',
                                                padding: 10,
                                                cornerRadius: 8,
                                                callbacks: {
                                                    label: (context) => `Events: ${context.parsed ?? 0}`
                                                }
                                            }
                                        }
                                    }}
                                />
                            </div>
                        </div>
                        <div className="mt-4 max-h-24 overflow-y-auto pr-1">
                            <div className="flex flex-wrap gap-x-5 gap-y-2">
                                {systemLabels.map((label, idx) => (
                                    <div key={`system-legend-${label}-${idx}`} className="flex items-center gap-2 text-sm text-gray-600">
                                        <span
                                            className="inline-block w-3 h-3 rounded-full"
                                            style={{ backgroundColor: systemColors[idx] }}
                                        />
                                        <span className="truncate max-w-[220px]" title={`${label}: ${systemValues[idx]}`}>
                                            {label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            }
        }

        const customChartItems = React.isValidElement(normalizedCustomCharts)
            ? [normalizedCustomCharts]
            : (Array.isArray(normalizedCustomCharts) ? normalizedCustomCharts : []);

        const allChartItems = [
            ...customChartItems,
            ...charts
        ];

        if (allChartItems.length === 0) return null;

        if (!isMobile) {
            const keyOf = (item) => String(item?.key || '');
            const truckChart = allChartItems.find((item) => keyOf(item).includes('trucks'));
            const timelineChart = allChartItems.find((item) => keyOf(item).includes('timeline'));
            const systemChart = allChartItems.find((item) => keyOf(item).includes('systems'));
            const primaryTopChart = truckChart || allChartItems.find((item) => {
                const key = keyOf(item);
                return !key.includes('timeline') && !key.includes('systems');
            });

            // Preferred desktop arrangement:
            // 1) Primary chart (usually Top Units/Drivers bar) full width
            // 2) Timeline + System split row
            // 3) Any extra/custom charts below in regular grid
            if (primaryTopChart && (timelineChart || systemChart)) {
                const usedKeys = new Set(
                    [primaryTopChart, timelineChart, systemChart]
                        .filter(Boolean)
                        .map((item) => keyOf(item))
                );
                const remainingCharts = allChartItems.filter((item) => !usedKeys.has(keyOf(item)));

                return (
                    <div className="space-y-4">
                        <div className="w-full min-w-0">{primaryTopChart}</div>
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                            {timelineChart && <div className="w-full min-w-0">{timelineChart}</div>}
                            {systemChart && <div className="w-full min-w-0">{systemChart}</div>}
                        </div>
                        {remainingCharts.length > 0 && (
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                {remainingCharts.map((item, idx) => (
                                    <div key={`chart-grid-extra-${idx}`} className="w-full min-w-0">
                                        {item}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            }

            return (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    {allChartItems.map((item, idx) => (
                        <div key={`chart-grid-${idx}`} className="w-full min-w-0">
                            {item}
                        </div>
                    ))}
                </div>
            );
        }

        return (
            <div className="space-y-3">
                <div
                    ref={chartCarouselRef}
                    onScroll={handleChartCarouselScroll}
                    className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-1 px-1 pb-2"
                >
                    {allChartItems.map((item, idx) => (
                        <div
                            key={`chart-slide-${idx}`}
                            data-carousel-item="chart"
                            className="w-full min-w-full snap-start shrink-0"
                        >
                            {item}
                        </div>
                    ))}
                </div>
                {allChartItems.length > 1 && (
                    <div className="flex items-center justify-center gap-2">
                        {allChartItems.map((_, idx) => (
                            <button
                                key={`chart-dot-${idx}`}
                                type="button"
                                onClick={() => scrollToChart(idx)}
                                aria-label={`Go to chart ${idx + 1}`}
                                className={`h-2 rounded-full transition-all ${chartCarouselIndex === idx ? 'w-5 bg-blue-500' : 'w-2 bg-gray-300'}`}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-4">
            {/* View Tabs */}
            <div className="flex items-center gap-2 border-b border-gray-200 overflow-x-auto scrollbar-hide">
                <button
                    onClick={() => setActiveView('table')}
                    className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors shrink-0 ${currentView === 'table'
                        ? 'border-blue-600 text-blue-600 font-medium'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                        }`}
                >
                    <Table className="w-4 h-4" />
                    Table
                </button>

                {enableCharts && (
                    <button
                        onClick={() => setActiveView('charts')}
                        className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors shrink-0 ${currentView === 'charts'
                            ? 'border-blue-600 text-blue-600 font-medium'
                            : 'border-transparent text-gray-600 hover:text-gray-900'
                            }`}
                    >
                        <BarChart3 className="w-4 h-4" />
                        Charts
                    </button>
                )}

                {enableGrouping && (
                    <button
                        onClick={() => setActiveView('grouped')}
                        className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors shrink-0 ${currentView === 'grouped'
                            ? 'border-blue-600 text-blue-600 font-medium'
                            : 'border-transparent text-gray-600 hover:text-gray-900'
                            }`}
                    >
                        <GitMerge className="w-4 h-4" />
                        Grouped
                    </button>
                )}
            </div>

            {/* View Content */}
            <div>
                {currentView === 'table' && (
                    <DataTable data={events} onExport={onExport} />
                )}

                {currentView === 'charts' && (
                    <div className="space-y-4">
                        {renderCharts()}
                    </div>
                )}

                {currentView === 'grouped' && (
                    <GroupedView
                        data={events}
                        aggregations={aggregations}
                        summary={summary}
                    />
                )}
            </div>
        </div>
    );
};

export default DataVisualization;
